import i18n from "../../../i18n";import { Trans } from "react-i18next";import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  X,
  Check } from
'lucide-react';
import { AppLayout } from '../../../components/layout';
import { LoadingState, EmptyState } from '../../../components/ui';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { EditableChecklistItems, ChecklistStatusBadge } from '../components/EditableChecklistItems';
import sessionDB from '../../../utils/sessionDB';
import {
  fetchComplaintDetail,
  fetchCustomerLocationById,
  updateCustomerComplaint,
  buildUpdateComplaintPayload,
  buildComplaintChecklistEntry,
  hasTechnicianAccepted,
  getComplaintStatusKey,
  fetchChecklistItemsSplitByWho,
  getComplaintChecklistSubmissions,
  findLatestChecklistSubmission,
  getChecklistSubmissionStatus,
  formatCallTypeLabel,
  mapQuotation,
  mapQuotations } from
'../../../services/fsmService';
import { getQuotationById, getQuotationsForComplaintId } from '../../../services/quotationService';
import { PageBackLink } from '../../../components/layout/PageBackLink';
import { translateTicketStatus, translateTicketPriority, getPriorityClass } from '../data/ticketConstants';
import { QuotationDetailsModal } from '../components/QuotationDetailsModal';

const CHECKLIST_STATUS_OPTIONS = [
'Pending',
'In Progress',
'Completed',
'Not Applicable'];

const CLOSURE_MODE_LABEL_KEYS = {
  Email: 'common.email',
  Phone: 'common.phone',
  Whatsapp: 'fsm.whatsapp'
};

function ReadOnlyField({ label, children }) {
  return (
    <div className="space-y-1">
      <span className="block text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
        {label}
      </span>
      <div className="text-sm text-foreground/90 whitespace-pre-wrap break-words">
        {children}
      </div>
    </div>);

}

function formatClosureModeLabel(mode) {
  const key = CLOSURE_MODE_LABEL_KEYS[String(mode || '').trim()];
  if (key) return i18n.t(key);
  return String(mode || '').trim() || '—';
}

function formatRatingDisplay(value) {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `${num} / 5`;
}

function normalizeChecklistStatus(value) {
  const raw = String(value || '').trim();
  if (!raw) return 'Pending';
  const match = CHECKLIST_STATUS_OPTIONS.find(
    (opt) => opt.toLowerCase() === raw.toLowerCase()
  );
  return match || 'Pending';
}

function formatAcceptanceTime(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
  const h12 = (d.getHours() + 11) % 12 + 1;
  return `${day} ${months[d.getMonth()]} ${d.getFullYear()}, ${String(h12).padStart(2, '0')}:${mm} ${ampm}`;
}

/** Display date, or date + time when present (supports " " or "T" separators). */
function formatDisplayDate(value) {
  if (!value) return '—';
  const raw = String(value).trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
  if (!match) return raw;
  const [, y, m, d, hh, mm] = match;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const label = `${d} ${months[parseInt(m, 10) - 1]} ${y}`;
  return hh != null && mm != null ? `${label}, ${hh}:${mm}` : label;
}

function getScheduledVisitDates(ticket) {
  const visits = Array.isArray(ticket?.scheduledVisits) && ticket.scheduledVisits.length > 0 ?
  ticket.scheduledVisits :
  Array.isArray(ticket?.raw?.scheduledVisits) ?
  ticket.raw.scheduledVisits :
  [];

  if (visits.length > 0) {
    const sorted = [...visits].map((v) => v?.visitDate).filter(Boolean).sort((a, b) => 
    {
      const ta = Date.parse(String(a).replace(' ', 'T')) || 0;
      const tb = Date.parse(String(b).replace(' ', 'T')) || 0;
      return ta - tb;
    });
    const seen = new Set();
    return sorted.filter((d) => {
      const key = String(d).replace(' ', 'T').slice(0, 16);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  const fallback =
  ticket?.visitDate ||
  ticket?.scheduledDate ||
  ticket?.raw?.scheduledDate ||
  ticket?.raw?.visitDate ||
  '';
  return fallback ? [fallback] : [];
}

export function FsmTechnicianTicketPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  // Defer fetch so React Strict Mode's immediate remount cancels the first
  // scheduled call — avoids duplicate detail API requests on navigate/mount.
  useEffect(() => {
    if (!id) return;

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      if (controller.signal.aborted) return;

      (async () => {
        setLoading(true);
        setLoadError('');
        try {
          const detail = await fetchComplaintDetail(id);
          if (controller.signal.aborted) return;
          if (!detail) {
            setTicket(null);
            setLoadError('Complaint not found.');
          } else {
            setTicket(detail);
          }
        } catch (err) {
          console.error('Failed to load complaint for technician:', err);
          if (!controller.signal.aborted) {
            setTicket(null);
            setLoadError(
              err.status === 401 ?
              'Session expired. Please refresh and try again.' :
              err.message || 'Failed to load complaint details.'
            );
          }
        } finally {
          if (!controller.signal.aborted) setLoading(false);
        }
      })();
    }, 0);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [id]);

  // --- Location details for Customer Details ---
  const [locationDetail, setLocationDetail] = useState(null);

  useEffect(() => {
    if (!ticket?.locationId) {
      setLocationDetail(null);
      return;
    }

    const locationId = ticket.locationId;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      if (controller.signal.aborted) return;

      (async () => {
        try {
          const detail = await fetchCustomerLocationById(locationId);
          if (controller.signal.aborted) return;
          setLocationDetail(detail);
        } catch (err) {
          console.error('Failed to load location details:', err);
          if (!controller.signal.aborted) setLocationDetail(null);
        }
      })();
    }, 0);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [ticket?.locationId]);

  // Complaint / customer fields from the same detail API as manager review
  const address =
  locationDetail?.addressDisplay ||
  ticket?.locationName ||
  ticket?.customerSub ||
  '—';
  const siteType = locationDetail?.siteType || '—';
  const system = ticket?.system || ticket?.productServiceName || '—';
  const poc = ticket?.pointOfContact || '—';
  const contact = ticket?.contactNumber || '—';
  const email = ticket?.email || '—';
  const complaintMode = ticket?.complaintMode || '—';
  const complaintDate = ticket?.complaintRecievedOn || ticket?.complaintDate ?
  formatDisplayDate(ticket.complaintRecievedOn || ticket.complaintDate) :
  '—';
  const complaintType = ticket?.complaintType || ticket?.complaintTypeName || '—';
  const description = ticket?.description || '—';
  const warrantyPeriod = ticket?.warrantyType ?
  String(ticket.warrantyType).replace(/_/g, ' ') :
  '—';
  const callType = formatCallTypeLabel(ticket?.callType || ticket?.raw?.callType) || '—';
  const scheduledVisitDates = getScheduledVisitDates(ticket);
  const installationCode = ticket?.assetName || '—';
  // --- Technician Actions States ---
  const [jobAccepted, setJobAccepted] = useState(false);
  const [acceptanceTime, setAcceptanceTime] = useState('');
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (!ticket) return;
    const accepted = hasTechnicianAccepted(ticket);
    setJobAccepted(accepted);
    if (accepted && !acceptanceTime) {
      setAcceptanceTime(formatAcceptanceTime());
    }
  }, [ticket, acceptanceTime]);

  // --- Resolution State ---
  const [resolution, setResolution] = useState('');

  useEffect(() => {
    if (!ticket) return;
    const remarks =
      ticket.technicianRemarks ??
      ticket.techRemarks ??
      ticket.remarks ??
      ticket.raw?.technicianRemarks ??
      ticket.raw?.techRemarks ??
      ticket.raw?.remarks ??
      '';
    const text = String(remarks || '').trim();
    if (text) setResolution(text);
  }, [ticket]);

  // --- Checklists (tech): Internal after accept (Scheduled), Field after In Progress ---
  const [internalChecklistItems, setInternalChecklistItems] = useState([]);
  const [fieldChecklistItems, setFieldChecklistItems] = useState([]);
  const [checklistRemarks, setChecklistRemarks] = useState({});
  const [checklistsLoading, setChecklistsLoading] = useState(false);
  const [checklistsReady, setChecklistsReady] = useState(false);
  const [checklistsError, setChecklistsError] = useState('');
  const [checklistSaving, setChecklistSaving] = useState(false);

  // --- Quotations ---
  const [quotations, setQuotations] = useState([]);
  const [quotationsLoading, setQuotationsLoading] = useState(false);
  const [quotationsError, setQuotationsError] = useState('');
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [quotationDetailOpen, setQuotationDetailOpen] = useState(false);

  // --- UI feedback state ---
  const [toast, setToast] = useState(null);

  const showToastMsg = (msg, type = 'success') => {
    setToast({ message: msg, type });
  };

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const statusKey = getComplaintStatusKey(ticket);
  const isAssigned = statusKey === 'assigned';
  const isAcceptedWaiting = statusKey === 'accepted';
  const isScheduledStatus = statusKey === 'scheduled';
  const isInProgress = statusKey === 'in_progress';
  const isQuotationSubmitted = statusKey === 'quotation_submitted';
  const isQuotationAccepted = statusKey === 'quotation_accepted';
  const isQuotationPhase = isQuotationSubmitted || isQuotationAccepted;
  const closureAlreadyRequested =
  statusKey === 'closure_requested' ||
  statusKey === 'closure_verified' ||
  statusKey === 'closed';
  const isClosureVerifiedStatus = statusKey === 'closure_verified';
  const isClosedTicket = statusKey === 'closed';
  const canShowClosureReason = isQuotationAccepted || closureAlreadyRequested;

  const listPath = '/fsm/customer-complaints';
  const redirectingRef = useRef(false);
  const redirectToList = () => {
    if (redirectingRef.current) return;
    redirectingRef.current = true;
    navigate(listPath, { replace: true });
  };

  // Phase 1 — before accept: Accept / Reject only (no checklists)
  const canRespondToAssignment =
  Boolean(ticket) &&
  isAssigned &&
  !jobAccepted &&
  ticket?.isAccepted !== true &&
  ticket?.raw?.isAccepted !== true &&
  ticket?.raw?.technicianAccepted !== true;

  const isChecklistItemDone = (status) => {
    const normalized = normalizeChecklistStatus(status);
    return normalized === 'Completed' || normalized === 'Not Applicable';
  };

  // Scheduled + internal already saved (Completed/N/A) → ready for "Mark as In Progress"
  const isInternalChecklistSavedAndDone = (() => {
    if (!checklistsReady) return false;
    const existing = getComplaintChecklistSubmissions(ticket);
    if (internalChecklistItems.length === 0) {
      // No template items — allow Mark as In Progress
      return true;
    }
    return internalChecklistItems.every((item) => {
      const prior = findLatestChecklistSubmission(
        existing,
        item.checklistItemId ?? item.id,
        'Internal Asset'
      );
      return isChecklistItemDone(getChecklistSubmissionStatus(prior));
    });
  })();

  // Field checklist completion from saved complaint rows (not live form edits)
  const isFieldChecklistSavedAndDone = (() => {
    if (!checklistsReady) return false;
    const existing = getComplaintChecklistSubmissions(ticket);
    if (fieldChecklistItems.length === 0) return true;
    return fieldChecklistItems.every((item) => {
      const prior = findLatestChecklistSubmission(
        existing,
        item.checklistItemId ?? item.id,
        'Field Service'
      );
      return isChecklistItemDone(getChecklistSubmissionStatus(prior));
    });
  })();

  const inScheduledWorkPhase =
  !canRespondToAssignment &&
  isScheduledStatus &&
  !isInProgress &&
  !closureAlreadyRequested;

  // Phase 2 — Scheduled, internal not yet saved: editable Internal Asset checklist
  const showInternalEditable =
  inScheduledWorkPhase &&
  checklistsReady &&
  !isInternalChecklistSavedAndDone;

  // Phase 2b — Scheduled, internal saved: read-only + Mark as In Progress
  const showMarkInProgress =
  inScheduledWorkPhase &&
  checklistsReady &&
  isInternalChecklistSavedAndDone;

  // Phase 3+ — In Progress / quotation / closure: internal read-only; also while waiting to mark in progress
  const showInternalReadOnly =
  showMarkInProgress || isInProgress || isQuotationPhase || closureAlreadyRequested;

  // Field checklist only after ticket is In Progress
  const showFieldChecklist = isInProgress || isQuotationPhase || closureAlreadyRequested;
  const showFieldEditable =
  isInProgress && !closureAlreadyRequested && !isQuotationPhase && !isFieldChecklistSavedAndDone;
  const showFieldReadOnly =
  showFieldChecklist && (isFieldChecklistSavedAndDone || isQuotationPhase || closureAlreadyRequested);

  const showQuotationSection =
  (showFieldReadOnly && isFieldChecklistSavedAndDone) || isQuotationPhase;
  const canRaiseQuotation =
  (isInProgress || isQuotationSubmitted) &&
  isFieldChecklistSavedAndDone &&
  !closureAlreadyRequested;
  const complaintIdForQuotations =
    ticket?.complaintId ??
    ticket?.id ??
    ticket?.raw?.complaintId ??
    id;
  const hasQuotations = quotations.length > 0;

  useEffect(() => {
    if (!showQuotationSection || complaintIdForQuotations == null || complaintIdForQuotations === '') {
      return undefined;
    }

    const nested = mapQuotations(
      Array.isArray(ticket?.quotations) ?
      ticket.quotations :
      Array.isArray(ticket?.raw?.quotations) ?
      ticket.raw.quotations :
      Array.isArray(ticket?.raw?.quotationList) ?
      ticket.raw.quotationList :
      []
    );

    let cancelled = false;
    setQuotationsLoading(true);
    setQuotationsError('');
    if (nested.length > 0) setQuotations(nested);

    getQuotationsForComplaintId(complaintIdForQuotations)
      .then((list) => {
        if (cancelled) return;
        const mapped = mapQuotations(list);
        setQuotations(mapped.length > 0 ? mapped : nested);
      })
      .catch((err) => {
        if (cancelled) return;
        setQuotations(nested);
        if (nested.length === 0) {
          setQuotationsError(err.message || 'Failed to load quotations.');
        }
      })
      .finally(() => {
        if (!cancelled) setQuotationsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showQuotationSection, complaintIdForQuotations, ticket?.quotations, ticket?.raw?.quotations, ticket?.raw?.quotationList]);

  const handleOpenQuotation = async (quotation) => {
    setSelectedQuotation(quotation);
    setQuotationDetailOpen(true);

    const quotationId = quotation?.id || quotation?.quotationId;
    if (!quotationId) return;

    try {
      const result = await getQuotationById(quotationId);
      const payload =
        result?.data && typeof result.data === 'object' && !Array.isArray(result.data) ?
        result.data :
        Array.isArray(result?.data) ?
        result.data[0] :
        result;
      const detailed = mapQuotation(payload);
      if (detailed && (detailed.id || detailed.quotationNumber)) {
        setSelectedQuotation((prev) => ({
          ...prev,
          ...detailed,
          lineItems:
            Array.isArray(detailed.lineItems) && detailed.lineItems.length > 0 ?
            detailed.lineItems :
            prev?.lineItems || []
        }));
      }
    } catch (err) {
      console.error('Failed to load quotation details:', err);
    }
  };

  const handleRaiseQuotation = () => {
    const complaintId = complaintIdForQuotations;
    if (complaintId == null || complaintId === '') return;

    navigate(
      `/sales/quotations/quotation/create?complaintId=${encodeURIComponent(complaintId)}`,
      {
        state: {
          complaintId,
          returnTo: `/fsm/customer-complaints/${ticket?.id ?? id}`,
          prefill: {
            customerId: ticket?.customerId ?? ticket?.raw?.customerId ?? null,
            customerName:
              ticket?.customerName && ticket.customerName !== '—' ?
              ticket.customerName :
              '',
            locationId: ticket?.locationId ?? ticket?.raw?.locationId ?? null,
            locationName:
              ticket?.locationName ||
              locationDetail?.locationName ||
              locationDetail?.addressDisplay ||
              '',
            contactName: ticket?.pointOfContact || '',
            phone: ticket?.contactNumber || '',
            email: ticket?.email || ''
          }
        }
      }
    );
  };

  const formatQuotationAmount = (amount, currency) => {
    if (amount == null || amount === '') return '';
    const num = Number(amount);
    const formatted = Number.isFinite(num) ?
    num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) :
    String(amount);
    return currency ? `${currency} ${formatted}` : formatted;
  };

  // Resolution / closure only after quotation is accepted
  const showResolutionSection = canShowClosureReason;
  const canRequestClosure =
  isQuotationAccepted &&
  isInternalChecklistSavedAndDone &&
  isFieldChecklistSavedAndDone &&
  !closureAlreadyRequested;

  const loadChecklists =
  showInternalEditable ||
  showInternalReadOnly ||
  showFieldChecklist ||
  inScheduledWorkPhase;

  // Load checklists for the current phase and hydrate statuses from the
  // complaint detail payload (`checklistItems` from GET /customer-complaints/{id}).
  // Use latest row per checklistItemId (API may append duplicates on re-save).
  const savedChecklistKey = (() => {
    const submissions = getComplaintChecklistSubmissions(ticket);
    if (submissions.length === 0) return '';
    const byKey = new Map();
    for (const entry of submissions) {
      const id = entry?.checklistItemId ?? entry?.id;
      if (id == null) continue;
      const who = String(entry?.remarks ?? entry?.whoToCheck ?? '')
      .trim()
      .toLowerCase();
      const mapKey = `${id}::${who}`;
      const prev = byKey.get(mapKey);
      if (!prev) {
        byKey.set(mapKey, entry);
        continue;
      }
      const prevRid = Number(prev.recordId) || 0;
      const curRid = Number(entry.recordId) || 0;
      if (curRid > prevRid) {
        byKey.set(mapKey, entry);
        continue;
      }
      if (curRid === prevRid) {
        const prevAt = Date.parse(prev.checkedAt || '') || 0;
        const curAt = Date.parse(entry.checkedAt || '') || 0;
        if (curAt >= prevAt) byKey.set(mapKey, entry);
      }
    }
    return [...byKey.values()]
    .map(
      (e) =>
      `${e.checklistItemId ?? e.id}:${getChecklistSubmissionStatus(e)}:${e.recordId ?? ''}`
    )
    .join('|');
  })();

  useEffect(() => {
    const complaintTypeId =
    ticket?.complaintTypeId ??
    ticket?.raw?.complaintTypeId ??
    ticket?.raw?.complaint?.complaintTypeId ??
    null;
    if (!loadChecklists || complaintTypeId == null || complaintTypeId === '') {
      setInternalChecklistItems([]);
      setFieldChecklistItems([]);
      setChecklistsReady(false);
      return;
    }

    let cancelled = false;
    setChecklistsLoading(true);
    setChecklistsReady(false);
    setChecklistsError('');

    fetchChecklistItemsSplitByWho(complaintTypeId)
    .then(({ internal, field }) => {
      if (cancelled) return;
      setInternalChecklistItems(internal);
      setFieldChecklistItems(field);

      const existing = getComplaintChecklistSubmissions(ticket);
      setChecklistRemarks((prev) => {
        const remarksMap = {};
        [...internal, ...field].forEach((item) => {
          const key = String(item.checklistItemId ?? item.id);
          const whoToCheck = String(
            item.whoToCheck || item.raw?.whoToCheck || ''
          ).trim();
          const prior = findLatestChecklistSubmission(
            existing,
            item.checklistItemId ?? item.id,
            whoToCheck
          );
          const savedStatus = getChecklistSubmissionStatus(prior);
          if (savedStatus) {
            remarksMap[key] = normalizeChecklistStatus(savedStatus);
          } else if (prev[key]) {
            remarksMap[key] = prev[key];
          } else {
            remarksMap[key] = 'Pending';
          }
        });
        return remarksMap;
      });
      setChecklistsReady(true);
    })
    .catch((err) => {
      console.error('Failed to load checklists:', err);
      if (!cancelled) {
        setInternalChecklistItems([]);
        setFieldChecklistItems([]);
        setChecklistsReady(true);
        setChecklistsError(
          err.status === 401 ?
          'Session expired. Please refresh and try again.' :
          err.message || 'Failed to load checklists.'
        );
      }
    })
    .finally(() => {
      if (!cancelled) setChecklistsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [loadChecklists, ticket, savedChecklistKey]);

  const buildChecklistEntries = (items) => {
    const existing = getComplaintChecklistSubmissions(ticket);
    const complaintId =
    ticket?.complaintId ??
    ticket?.id ??
    ticket?.raw?.complaintId ??
    ticket?.raw?.id ??
    null;

    return items.map((item) => {
      const key = String(item.checklistItemId ?? item.id);
      const status = normalizeChecklistStatus(checklistRemarks[key]);
      const whoToCheck = String(
        item.whoToCheck || item.raw?.whoToCheck || ''
      ).trim();
      const prior = findLatestChecklistSubmission(
        existing,
        item.checklistItemId ?? item.id,
        whoToCheck
      );

      return buildComplaintChecklistEntry({
        recordId: prior?.recordId ?? null,
        complaintId,
        checklistItemId: item.checklistItemId ?? item.id,
        checkedByRole: 'Technician',
        checkedBy: sessionDB.getEmpId(),
        isChecked: isChecklistItemDone(status),
        status,
        remarks: whoToCheck || String(prior?.remarks || '').trim(),
        checkedAt: undefined
      });
    });
  };

  const setItemStatus = (itemKey, value) => {
    setChecklistRemarks((prev) => ({
      ...prev,
      [itemKey]: value
    }));
  };

  const setAllItemsStatus = (items, status) => {
    setChecklistRemarks((prev) => {
      const next = { ...prev };
      items.forEach((item) => {
        next[String(item.checklistItemId ?? item.id)] = status;
      });
      return next;
    });
  };

  const validateChecklistStatuses = (items, label, { requireDone = false } = {}) => {
    if (!items || items.length === 0) return true;
    const missing = items.find((item) => {
      const key = String(item.checklistItemId ?? item.id);
      const status = normalizeChecklistStatus(checklistRemarks[key]);
      if (!CHECKLIST_STATUS_OPTIONS.includes(status) || status === 'Pending') {
        return true;
      }
      if (requireDone && !isChecklistItemDone(status)) return true;
      return false;
    });
    if (missing) {
      showToastMsg(
        requireDone ?
        `Mark every ${label} item as Completed or Not Applicable before continuing.` :
        `Please update status for every ${label} item before continuing.`,
        'error'
      );
      return false;
    }
    return true;
  };

  /** Save Internal Asset checklist while Scheduled — status stays Scheduled, then return to list. */
  const handleSaveInternalChecklist = async () => {
    if (!ticket?.id || checklistSaving) return;
    if (
    internalChecklistItems.length > 0 &&
    !validateChecklistStatuses(internalChecklistItems, 'Internal Asset', {
      requireDone: true
    }))
    {
      return;
    }

    setChecklistSaving(true);
    try {
      const complaintChecklist = buildChecklistEntries(internalChecklistItems);
      const payload = buildUpdateComplaintPayload(ticket, {
        technicianId: ticket.technicianId ?? sessionDB.getEmpId(),
        complaintStatus: 'Scheduled',
        technicianRemarks:
          resolution ||
          ticket.technicianRemarks ||
          ticket.techRemarks ||
          ticket.raw?.technicianRemarks ||
          ticket.raw?.techRemarks ||
          ticket.raw?.remarks ||
          '',
        complaintChecklist
      });

      await updateCustomerComplaint(ticket.id, payload);
      showToastMsg('Internal checklist saved.');
      redirectToList();
    } catch (err) {
      console.error('Failed to save internal checklist:', err);
      showToastMsg(
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to save checklist.',
        'error'
      );
    } finally {
      setChecklistSaving(false);
    }
  };

  /** Manual transition: Scheduled → In_Progress, then Field Service checklist appears. */
  const handleMarkInProgress = async () => {
    if (!ticket?.id || actionLoading) return;

    setActionLoading('in_progress');
    try {
      const payload = buildUpdateComplaintPayload(ticket, {
        technicianId: ticket.technicianId ?? sessionDB.getEmpId(),
        complaintStatus: 'In_Progress',
        technicianRemarks:
          resolution ||
          ticket.technicianRemarks ||
          ticket.techRemarks ||
          ticket.raw?.technicianRemarks ||
          ticket.raw?.techRemarks ||
          ticket.raw?.remarks ||
          ''
      });
      await updateCustomerComplaint(ticket.id, payload);
      const refreshed = await fetchComplaintDetail(ticket.id);
      setTicket(
        refreshed ?
        {
          ...refreshed,
          status: 'In Progress',
          statusRaw: 'In_Progress',
          raw: {
            ...(refreshed.raw || {}),
            complaintStatus: 'In_Progress'
          }
        } :
        {
          ...ticket,
          status: 'In Progress',
          statusRaw: 'In_Progress',
          raw: {
            ...(ticket.raw || {}),
            complaintStatus: 'In_Progress'
          }
        }
      );
      showToastMsg('Ticket marked as In Progress.');
    } catch (err) {
      console.error('Failed to mark ticket In Progress:', err);
      showToastMsg(
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to update ticket status.',
        'error'
      );
    } finally {
      setActionLoading('');
    }
  };

  /** Save Field Service checklist while In Progress — then return to list. */
  const handleSaveFieldChecklist = async () => {
    if (!ticket?.id || checklistSaving) return;
    if (fieldChecklistItems.length === 0) {
      showToastMsg('No field service checklist items to save.', 'error');
      return;
    }
    if (!validateChecklistStatuses(fieldChecklistItems, 'Field Service')) return;

    setChecklistSaving(true);
    try {
      const complaintChecklist = buildChecklistEntries(fieldChecklistItems);

      const payload = buildUpdateComplaintPayload(ticket, {
        technicianId: ticket.technicianId ?? sessionDB.getEmpId(),
        complaintStatus: 'In_Progress',
        technicianRemarks:
          resolution ||
          ticket.technicianRemarks ||
          ticket.techRemarks ||
          ticket.raw?.technicianRemarks ||
          ticket.raw?.techRemarks ||
          ticket.raw?.remarks ||
          '',
        complaintChecklist
      });

      await updateCustomerComplaint(ticket.id, payload);
      showToastMsg('Field service checklist saved.');
      redirectToList();
    } catch (err) {
      console.error('Failed to save field checklist:', err);
      showToastMsg(
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to save checklist.',
        'error'
      );
    } finally {
      setChecklistSaving(false);
    }
  };

  // --- Technician job acceptance ---
  // Accept → Accepted → return to list; manager then schedules the visit
  const handleAcceptJob = async () => {
    if (!ticket?.id || actionLoading) return;

    setActionLoading('accept');
    try {
      await updateCustomerComplaint(
        ticket.id,
        buildUpdateComplaintPayload(ticket, {
          technicianId: ticket.technicianId ?? sessionDB.getEmpId(),
          complaintStatus: 'Accepted'
        })
      );

      setTicket((prev) =>
      prev ?
      {
        ...prev,
        status: 'Accepted',
        statusRaw: 'Accepted',
        isAccepted: true,
        raw: prev.raw ?
        { ...prev.raw, complaintStatus: 'Accepted', isAccepted: true } :
        prev.raw
      } :
      prev
      );
      setJobAccepted(true);
      setAcceptanceTime(formatAcceptanceTime());
      showToastMsg('Job accepted. Waiting for the manager to schedule the visit.');
      redirectToList();
    } catch (err) {
      console.error('Failed to accept ticket:', err);
      showToastMsg(
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to accept ticket.',
        'error'
      );
    } finally {
      setActionLoading('');
    }
  };

  // --- Technician job rejection ---
  const handleRejectConfirm = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      showToastMsg('Please enter a reason for rejection.', 'error');
      return;
    }
    if (!ticket?.id || actionLoading) return;

    setActionLoading('reject');
    try {
      await updateCustomerComplaint(
        ticket.id,
        buildUpdateComplaintPayload(ticket, {
          technicianId: ticket.technicianId ?? sessionDB.getEmpId(),
          complaintStatus: 'Under_Review',
          technicianRemarks: reason
        })
      );
      setIsRejectOpen(false);
      setRejectReason('');
      showToastMsg('Job rejected. Returned to manager for review.', 'error');
      redirectToList();
    } catch (err) {
      console.error('Failed to reject ticket:', err);
      showToastMsg(
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to reject ticket.',
        'error'
      );
    } finally {
      setActionLoading('');
    }
  };

  // --- Submit closure request ---
  const handleRequestClosure = async () => {
    if (!resolution.trim()) {
      showToastMsg('Please fill in the Resolution / Reason for Closure summary first.', 'error');
      return;
    }
    if (!ticket?.id || actionLoading) return;

    setActionLoading('closure');
    try {
      const payload = buildUpdateComplaintPayload(ticket, {
        technicianId: ticket.technicianId ?? sessionDB.getEmpId(),
        technicianRemarks: resolution.trim(),
        complaintStatus: 'Closure_Requested'
      });
      await updateCustomerComplaint(ticket.id, payload);
      showToastMsg('Closure request submitted successfully to the Service Manager.');
      redirectToList();
    } catch (err) {
      console.error('Failed to request closure:', err);
      showToastMsg(
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to request closure.',
        'error'
      );
    } finally {
      setActionLoading('');
    }
  };

  // --- Styles ---
  const cardCls = 'bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.22)] border border-[var(--color-text-border)]/60 p-5 mb-5';
  const labelCls = 'text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1';
  const valCls = 'text-[13px] font-semibold text-foreground';

  const closureMode =
    ticket?.closureMode ||
    ticket?.raw?.closureMode ||
    '';
  const customerServiceRemarks = String(
    ticket?.customerClosureRemarks ||
      ticket?.customerServiceRemarks ||
      ticket?.raw?.customerClosureRemarks ||
      ticket?.raw?.customerServiceRemarks ||
      ''
  ).trim();
  const customerRating =
    ticket?.customerRating ??
    ticket?.customerRatings ??
    ticket?.raw?.customerRating ??
    ticket?.raw?.customerRatings ??
    null;
  const managerRemarks = String(
    ticket?.managerClosureRemarks ||
      ticket?.managerRemarks ||
      ticket?.raw?.managerClosureRemarks ||
      ticket?.raw?.managerRemarks ||
      ''
  ).trim();
  const managerRating =
    ticket?.managerRating ??
    ticket?.managerRatings ??
    ticket?.raw?.managerRating ??
    ticket?.raw?.managerRatings ??
    null;

  const hasCustomerServiceFeedback =
    Boolean(String(closureMode || '').trim()) ||
    Boolean(customerServiceRemarks) ||
    (customerRating != null && customerRating !== '');
  const hasManagerFeedback =
    Boolean(managerRemarks) ||
    (managerRating != null && managerRating !== '');
  const showClosureFeedback =
    closureAlreadyRequested && (hasCustomerServiceFeedback || hasManagerFeedback);

  if (loading) {
    return (
      <AppLayout>
        <LoadingState message="Loading ticket..." />
      </AppLayout>);

  }

  if (!ticket || loadError) {
    return (
      <AppLayout>
        <EmptyState
          title={i18n.t("validation.unableToLoadTicket")}
          message={loadError || 'Complaint not found.'} />
        
        <div className="mt-4 flex justify-center">
          <Button type="button" variant="outline" onClick={() => navigate('/fsm/customer-complaints')}>
            <Trans i18nKey="formActions.backToTickets" />
          </Button>
        </div>
      </AppLayout>);

  }

  return (
    <AppLayout>
      {/* Toast Alert */}
      {toast &&
      <div className={cn(
        "fixed bottom-6 right-6 z-50 p-4 rounded-xl shadow-lg flex items-center gap-3 animate-in fade-in slide-in-from-bottom-5 duration-300",
        toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'
      )}>
          <span className="text-sm font-semibold">{toast.message}</span>
          <button onClick={() => setToast(null)} className="hover:opacity-85 text-white/90">
            <X className="h-4 w-4" />
          </button>
        </div>
      }


      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div className="flex flex-col gap-2.5">
                <div className="mt-1">
                  <PageBackLink to="/fsm/customer-complaints" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-[20px] font-bold text-foreground">
                      <Trans i18nKey="fsm.complaintNumber" /> - {ticket.complaintNo || '—'}
                    </h1>
                    {/* <span className={cn(
                 "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold",
                  [ticket.status] || "bg-[var(--color-primary)]/12 text-[var(--color-primary)]"
                )}>
                {ticket.status}
                </span> */}
                    
                  </div>
                  {/* <p className="text-xs font-semibold text-gray-500 mt-1">
               {ticket.customerName} &middot; {ticket.system} &middot; {complaintType}
              </p> */}
                </div>
              </div>
      
              <div className="shrink-0 flex items-center gap-2">
                <span className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border border-current/20",
            getPriorityClass(ticket.priority),
            "bg-current/5"
          )}>
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                  {translateTicketPriority(ticket.priority, i18n.t.bind(i18n)) || "—"}
                </span>
              </div>
            </div>
      

      <div className="max-w-3xl">

        {(showInternalEditable || showMarkInProgress) &&
        <div className="rounded-2xl border border-[var(--color-success)]/30 bg-[var(--color-success)]/5 px-4 sm:px-5 py-3.5 mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-success)]">
                <Check className="h-4 w-4 shrink-0" />
                <span>
                  {`Scheduled${acceptanceTime ? ` · accepted ${acceptanceTime}` : ''}`}
                </span>
              </div>
              {scheduledVisitDates.length > 0 &&
              <p className="text-xs text-[var(--color-text-secondary)] font-medium pl-6">
                  <Trans i18nKey="fsm.scheduledVisit" />:{' '}
                  {scheduledVisitDates.map((d) => formatDisplayDate(d)).join(' · ')}
                </p>
              }
            </div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[var(--color-success)]/12 text-[var(--color-success)]">
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              {translateTicketStatus('Scheduled', i18n.t.bind(i18n))}
            </span>
          </div>
        }

        {isInProgress &&
        <div className="rounded-2xl border border-sky-200 bg-sky-50/60 px-4 sm:px-5 py-3.5 mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-sky-800">
              <Check className="h-4 w-4 shrink-0" />
              <span><Trans i18nKey="fsm.inProgressCompleteFieldServiceChecklistToRequest" /></span>
            </div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-sky-100 text-sky-800">
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              {translateTicketStatus('In Progress', i18n.t.bind(i18n))}
            </span>
          </div>
        }

        {isQuotationSubmitted &&
        <div className="rounded-2xl border border-sky-200 bg-sky-50/60 px-4 sm:px-5 py-3.5 mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-sky-800">
              <Check className="h-4 w-4 shrink-0" />
              <span><Trans i18nKey="fsm.statusQuotationSubmitted" /></span>
            </div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-sky-100 text-sky-800">
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              {translateTicketStatus('Quotation_Submitted', i18n.t.bind(i18n))}
            </span>
          </div>
        }

        {isQuotationAccepted &&
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 px-4 sm:px-5 py-3.5 mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
              <Check className="h-4 w-4 shrink-0" />
              <span><Trans i18nKey="fsm.statusQuotationAccepted" /></span>
            </div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              {translateTicketStatus('Quotation_Accepted', i18n.t.bind(i18n))}
            </span>
          </div>
        }

        {/* SECTION: Complaint Details (same API fields as manager review) */}
        <div className={cardCls}>
          <div className="mb-4 pb-3 border-b border-gray-100">
            <h2 className="text-[14px] font-bold text-foreground"><Trans i18nKey="fsm.customerDetails" /></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3.5 mb-4">
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="common.customer" /></span>
              <span className={valCls}>{ticket.customerName || '—'}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="common.location4" /></span>
              <span className={valCls}>{address}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="common.siteType" /></span>
              <span className={valCls}>{siteType}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="fsm.pointOfContact" /></span>
              <span className={valCls}>{poc}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="common.contact3" /></span>
              <span className={valCls}>{contact}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="common.email" /></span>
              <span className={valCls}>{email}</span>
            </div>
            
            
          </div>
          
        </div>

        {/* SECTION: Product / service summary */}
        <div className={cardCls}>
          <div className="mb-4 pb-2 border-b border-gray-100">
            <h2 className="text-[14px] font-bold text-foreground"><Trans i18nKey="fsm.complaintDetails" /></h2>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3.5">
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="fsm.complaintMode" /></span>
              <span className={valCls}>{complaintMode}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="dateTime.complaintDate" /></span>
              <span className={valCls}>{complaintDate}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="fsm.scheduledVisit" /></span>
              <span className={valCls}>
                {scheduledVisitDates.length > 0 ?
                scheduledVisitDates.map((d) => formatDisplayDate(d)).join(' · ') :
                '—'}
              </span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="fsm.system" /></span>
              <span className={valCls}>{system}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="fsm.typeOfComplaint" /></span>
              <span className={valCls}>{complaintType}</span>
            </div>
            <div className="flex flex-col">
              <span className={labelCls}>Call Type</span>
              <span className={valCls}>{callType}</span>
            </div>
            
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="fsm.warranty" /></span>
              <span className={valCls}>{warrantyPeriod}</span>
            </div>
            {/* <div className="flex flex-col">
               <span className={labelCls}>AMC</span>
               <span className={valCls}>{amc}</span>
              </div> */}
            
            <div className="flex flex-col">
              <span className={labelCls}><Trans i18nKey="common.asset" /></span>
              <span className={valCls}>{installationCode}</span>
            </div>
            <div className="flex flex-col pt-3 border-t border-gray-100">
            <span className={labelCls}><Trans i18nKey="common.description2" /></span>
            <p className="text-[13px] font-semibold text-gray-600 mt-1 leading-relaxed">{description}</p>
          </div>
          </div>
        </div>

        {/* Phase 1 — Accept / Reject (no checklists) */}
        {canRespondToAssignment &&
        <div className={cardCls}>
            <div className="mb-4 pb-2 border-b border-gray-100">
              <h2 className="text-[14px] font-bold text-foreground"><Trans i18nKey="fsm.jobAssignment" /></h2>
            </div>
            <div className="flex flex-col gap-4">
              <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
                <span className="font-bold text-foreground"><Trans i18nKey="fsm.accept" /></span> <Trans i18nKey="fsm.toTakeThisJobOr" />{' '}
                <span className="font-bold text-foreground"><Trans i18nKey="formActions.reject" /></span> <Trans i18nKey="fsm.toReturnItToTheManager" />
              </p>
              <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 sm:justify-end">
                <button
                type="button"
                disabled={Boolean(actionLoading)}
                onClick={() => setIsRejectOpen(true)}
                className="h-10 px-4 sm:px-5 rounded-xl border border-[var(--color-error)] text-[var(--color-error)] bg-white font-semibold text-sm hover:bg-[var(--color-error)]/5 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto">
                  <Trans i18nKey="formActions.rejectJob" />
                
              </button>
                <button
                type="button"
                disabled={Boolean(actionLoading)}
                onClick={handleAcceptJob}
                className="h-10 px-4 sm:px-5 rounded-xl bg-[var(--color-primary)] text-white font-semibold text-sm hover:bg-[var(--color-accent)] transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] w-full sm:w-auto">
                
                  {actionLoading === 'accept' ? i18n.t('fsm.accepting') : <Trans i18nKey="formActions.acceptJob" />}
                </button>
              </div>
            </div>
          </div>
        }

        {isAcceptedWaiting &&
        <div className={cardCls}>
            <h2 className="text-[14px] font-bold text-foreground mb-2"><Trans i18nKey="fsm.jobAssignment" /></h2>
            <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
              Job accepted. Waiting for the manager to schedule the visit.
            </p>
          </div>
        }

        {inScheduledWorkPhase && !checklistsReady &&
        <div className={cardCls}>
            <p className="text-xs text-[var(--color-text-secondary)] font-medium"><Trans i18nKey="fsm.loadingChecklist" /></p>
          </div>
        }

        {/* Phase 2 — Internal Asset Checklist (editable) while Scheduled, not yet saved */}
        {showInternalEditable &&
        <div className={cardCls}>
            <div className="mb-4 pb-2 border-b border-gray-100">
              <h2 className="text-[14px] font-bold text-foreground"><Trans i18nKey="fsm.internalAssetChecklist" /></h2>
              <p className="text-[11px] text-gray-400 font-medium mt-0.5">
                <Trans i18nKey="formActions.markEachItemCompletedOrNotApplicableThen" />
              </p>
            </div>

            {checklistsLoading ?
          <p className="text-xs text-[var(--color-text-secondary)] font-medium"><Trans i18nKey="fsm.loadingChecklist" /></p> :
          checklistsError ?
          <p className="text-xs text-[var(--color-error)] font-medium">{checklistsError}</p> :
          internalChecklistItems.length === 0 ?
          <p className="text-xs text-gray-400 italic mb-4"><Trans i18nKey="fsm.noInternalAssetItemsForThisComplaintType" /></p> :

          <div className="mb-4">
                <EditableChecklistItems
                  items={internalChecklistItems}
                  remarks={checklistRemarks}
                  normalizeStatus={normalizeChecklistStatus}
                  disabled={checklistSaving}
                  idPrefix="internal-checklist"
                  onToggleStatus={setItemStatus}
                  onSelectAllCompleted={(checked) =>
                    setAllItemsStatus(internalChecklistItems, checked ? 'Completed' : 'Pending')
                  }
                />
              </div>
          }

            <div className="flex justify-end pt-1 border-t border-gray-100">
              <button
              type="button"
              disabled={checklistSaving || checklistsLoading}
              onClick={handleSaveInternalChecklist}
              className="h-10 px-6 bg-[var(--color-primary)] hover:bg-[var(--color-accent)] text-white font-semibold text-sm rounded-xl transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto">
              
                {checklistSaving ? i18n.t('fsm.savingEllipsis') : <Trans i18nKey="fsm.saveChecklist" />}
              </button>
            </div>
          </div>
        }

        {/* Internal Asset Checklist (read-only) — after save (Scheduled) or In Progress / closure */}
        {showInternalReadOnly &&
        <div className={cardCls}>
            <div className="mb-4 pb-2 border-b border-gray-100">
              <h2 className="text-[14px] font-bold text-foreground"><Trans i18nKey="fsm.internalAssetChecklist" /></h2>
              <p className="text-[11px] text-gray-400 font-medium mt-0.5">
                <Trans i18nKey="fsm.completedDuringInitialInspection" />
              </p>
            </div>

            {checklistsLoading ?
          <p className="text-xs text-[var(--color-text-secondary)] font-medium"><Trans i18nKey="fsm.loadingChecklist" /></p> :
          checklistsError ?
          <p className="text-xs text-[var(--color-error)] font-medium">{checklistsError}</p> :
          internalChecklistItems.length === 0 ?
          <p className="text-xs text-gray-400 italic"><Trans i18nKey="fsm.noInternalAssetItemsForThisComplaintType" /></p> :

          <div className="flex flex-col gap-3">
                {internalChecklistItems.map((item, idx) => {
              const key = String(item.checklistItemId ?? item.id);
              const status = normalizeChecklistStatus(checklistRemarks[key]);
              return (
                <div
                  key={key}
                  className="border border-gray-100 rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 bg-gray-50/30 items-start sm:items-center">
                  
                      <p className="text-xs font-bold text-foreground min-w-0 flex-1">
                        <span className="text-gray-400 mr-1.5">{idx + 1}.</span>
                        {item.whatToCheck}
                      </p>
                      <ChecklistStatusBadge status={status} />
                    </div>);

            })}
              </div>
          }

            {showMarkInProgress &&
          <div className="flex justify-end pt-4 mt-2 border-t border-gray-100">
                <button
              type="button"
              disabled={Boolean(actionLoading) || checklistsLoading}
              onClick={handleMarkInProgress}
              className="h-10 px-6 bg-[var(--color-primary)] hover:bg-[var(--color-accent)] text-white font-semibold text-sm rounded-xl transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto">
              
                  {actionLoading === 'in_progress' ? i18n.t('fsm.updating') : <Trans i18nKey="fsm.markAsInProgress" />}
                </button>
              </div>
          }
          </div>
        }

        {/* Field Service Checklist — after Mark as In Progress */}
        {showFieldChecklist &&
        <div className={cardCls}>
            <div className="mb-4 pb-2 border-b border-gray-100">
              <h2 className="text-[14px] font-bold text-foreground"><Trans i18nKey="fsm.fieldServiceChecklist" /></h2>
              <p className="text-[11px] text-gray-400 font-medium mt-0.5">
                {showFieldReadOnly && !closureAlreadyRequested ?
              'All items completed. You can raise a quotation below.' :
              closureAlreadyRequested ?
              'Field service checklist (read-only).' :
              'Mark each item Completed or Not Applicable, then save. You can update again until every item is done.'}
              </p>
            </div>

            {checklistsLoading ?
          <p className="text-xs text-[var(--color-text-secondary)] font-medium"><Trans i18nKey="fsm.loadingChecklist" /></p> :
          checklistsError ?
          <p className="text-xs text-[var(--color-error)] font-medium">{checklistsError}</p> :
          fieldChecklistItems.length === 0 ?
          <p className="text-xs text-gray-400 italic"><Trans i18nKey="fsm.noFieldServiceItemsForThisComplaintType" /></p> :
          showFieldReadOnly ?
          <div className="flex flex-col gap-3">
                {fieldChecklistItems.map((item, idx) => {
              const key = String(item.checklistItemId ?? item.id);
              const status = normalizeChecklistStatus(checklistRemarks[key]);
              return (
                <div
                  key={key}
                  className="border border-gray-100 rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 bg-gray-50/30 items-start sm:items-center">
                  
                      <p className="text-xs font-bold text-foreground min-w-0 flex-1">
                        <span className="text-gray-400 mr-1.5">{idx + 1}.</span>
                        {item.whatToCheck}
                      </p>
                      <ChecklistStatusBadge status={status} />
                    </div>);

            })}
              </div> :

          <div className="flex flex-col gap-4">
                <EditableChecklistItems
                  items={fieldChecklistItems}
                  remarks={checklistRemarks}
                  normalizeStatus={normalizeChecklistStatus}
                  disabled={checklistSaving}
                  idPrefix="field-checklist"
                  onToggleStatus={setItemStatus}
                  onSelectAllCompleted={(checked) =>
                    setAllItemsStatus(fieldChecklistItems, checked ? 'Completed' : 'Pending')
                  }
                />

                {showFieldEditable ?
            <div className="flex justify-end pt-1">
                    <button
                type="button"
                disabled={checklistSaving}
                onClick={handleSaveFieldChecklist}
                className="h-10 px-6 bg-[var(--color-primary)] hover:bg-[var(--color-accent)] text-white font-semibold text-sm rounded-xl transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto">
                
                      {checklistSaving ? i18n.t('fsm.savingEllipsis') : <Trans i18nKey="fsm.saveChecklist" />}
                    </button>
                  </div> :
            null}
              </div>
          }
          </div>
        }

        {showQuotationSection &&
        <div className={cardCls}>
            <div className="mb-4 pb-2 border-b border-gray-100">
              <h2 className="text-[14px] font-bold text-foreground"><Trans i18nKey="fsm.quotation" /></h2>
            </div>
            {quotationsLoading ?
            <p className="text-xs text-[var(--color-text-secondary)] font-medium mb-4">Loading quotations…</p> :
            quotationsError && !hasQuotations ?
            <p className="text-xs text-[var(--color-error)] font-medium mb-4">{quotationsError}</p> :
            !hasQuotations ?
            <p className="text-xs text-gray-400 italic mb-4"><Trans i18nKey="fsm.noQuotationRaisedYet" /></p> :
            <ul className="mb-4 space-y-2">
                {quotations.map((quotation) => {
              const amountLabel = formatQuotationAmount(
                quotation.totalAmount,
                quotation.currency
              );
              const subtitle = [
              quotation.status,
              amountLabel]
              .filter(Boolean).join(' · ');
              return (
                <li key={quotation.id || quotation.quotationNumber}>
                    <button
                  type="button"
                  onClick={() => handleOpenQuotation(quotation)}
                  className="w-full text-left rounded-xl border border-[var(--color-text-border)]/60 bg-[var(--color-workspace)]/25 px-3 py-2.5 hover:border-[var(--color-accent)]/40 hover:bg-[var(--color-workspace)]/50 transition cursor-pointer">
                  
                      <div className="text-sm font-semibold text-foreground">
                        {quotation.quotationNumber || `#${quotation.id}`}
                      </div>
                      {subtitle ?
                  <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div> :
                  null}
                    </button>
                  </li>);

            })}
              </ul>}
            <div className="flex justify-end">
              <button
              type="button"
              onClick={handleRaiseQuotation}
              disabled={!canRaiseQuotation}
              className="h-10 px-6 bg-[var(--color-primary)] hover:bg-[var(--color-accent)] text-white font-semibold text-sm rounded-xl transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed">
                <Trans i18nKey="fsm.raiseQuotation" />
              </button>
            </div>
          </div>
        }

        {/* Resolution + Request Closure — only when every field item is Completed / N/A */}
        {showResolutionSection &&
        <>
        <div className={cardCls}>
          <div className="mb-4 pb-2 border-b border-gray-100">
            <h2 className="text-[14px] font-bold text-foreground"><Trans i18nKey="fsm.resolution" /></h2>
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-500 mb-1.5"><Trans i18nKey="fsm.resolutionReasonForClosure" /></label>
            <textarea
                placeholder={i18n.t("fsm.enterCompletedWorkDetailsPartsReplacedLoopTests")}
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                rows={4}
                disabled={closureAlreadyRequested || Boolean(actionLoading)}
                className="w-full px-3 py-2 border border-[var(--color-text-border)] rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/15 focus:border-[var(--color-accent)] transition resize-vertical disabled:opacity-60 disabled:bg-gray-50" />
              
          </div>
        </div>

        {!closureAlreadyRequested &&
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3 justify-end mb-8">
          <Button
              type="button"
              variant="outline"
              onClick={() => navigate(listPath)}
              className="h-10 px-6 rounded-xl border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 bg-white w-full sm:w-auto">
            <Trans i18nKey="formActions.cancel" />
          
            </Button>
          <button
              type="button"
              disabled={Boolean(actionLoading) || !canRequestClosure}
              onClick={handleRequestClosure}
              title={
              canRequestClosure ?
              undefined :
              'Quotation must be accepted before requesting closure'
              }
              className="h-10 px-6 bg-[var(--color-primary)] hover:bg-[var(--color-accent)] text-white font-semibold text-sm rounded-xl transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto">
              
            {actionLoading === 'closure' ? i18n.t('fsm.submitting') : <Trans i18nKey="fsm.requestTicketClosure" />}
          </button>
        </div>
          }

        {closureAlreadyRequested &&
          <div className="rounded-2xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5 px-4 sm:px-5 py-3.5 mb-5">
            <p className="text-sm font-semibold text-[var(--color-warning)]">
              {isClosedTicket ?
            <Trans i18nKey="fsm.statusClosed" /> :
            isClosureVerifiedStatus ?
            <Trans i18nKey="fsm.closureVerifiedWaitingForManagerToClose" /> :
            <Trans i18nKey="fsm.waitingForCustomerServiceVerification" />
            }
            </p>
          </div>
          }

        {showClosureFeedback &&
          <div className={cardCls}>
            <div className="mb-4 pb-2 border-b border-gray-100">
              <h2 className="text-[14px] font-bold text-foreground">
                <Trans i18nKey="fsm.closureVerification" />
              </h2>
            </div>
            <div className="space-y-4">
              {hasCustomerServiceFeedback ?
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-xl border border-[var(--color-text-border)]/40 px-3 py-3 bg-[var(--color-workspace)]/20">
                  <div className="sm:col-span-2 pb-1 border-b border-gray-100">
                    <h3 className="text-[13px] font-bold text-foreground">
                      <Trans i18nKey="fsm.customerVerification" />
                    </h3>
                  </div>
                  <ReadOnlyField label={i18n.t('fsm.closureMode')}>
                    {formatClosureModeLabel(closureMode)}
                  </ReadOnlyField>
                  <ReadOnlyField label={i18n.t('fsm.customerRatings')}>
                    {formatRatingDisplay(customerRating)}
                  </ReadOnlyField>
                  <div className="sm:col-span-2">
                    <ReadOnlyField label={i18n.t('fsm.customerServiceRemarks')}>
                      {customerServiceRemarks || '—'}
                    </ReadOnlyField>
                  </div>
                </div> :
              null}

              {hasManagerFeedback ?
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-xl border border-[var(--color-text-border)]/40 px-3 py-3 bg-[var(--color-workspace)]/20">
                  <div className="sm:col-span-2">
                    <ReadOnlyField label={i18n.t('common.managerRemarks')}>
                      {managerRemarks || '—'}
                    </ReadOnlyField>
                  </div>
                  <ReadOnlyField label={i18n.t('fsm.managerRatings')}>
                    {formatRatingDisplay(managerRating)}
                  </ReadOnlyField>
                </div> :
              null}
            </div>
          </div>
          }
          </>
        }

        {isInProgress && !isFieldChecklistSavedAndDone && !closureAlreadyRequested &&
        <p className="text-xs text-gray-400 font-medium mb-8">
            <Trans i18nKey="fsm.completeEveryFieldServiceItemCompletedOrNot" />
          </p>
        }

      </div>

      {/* --- CONFIRMATION DIALOG: REJECT --- */}
      {isRejectOpen &&
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl max-w-md w-full p-5 flex flex-col gap-4 animate-in slide-in-from-bottom-4 duration-300">
            <div>
              <h3 className="text-base font-bold text-foreground flex items-center gap-2">
                <X className="h-5 w-5 text-rose-600" />
                <Trans i18nKey="formActions.rejectAssignedJob" />
              </h3>
              <p className="text-xs text-gray-500 font-medium mt-1"><Trans i18nKey="validation.specifyReasonForJobRejectionRequired" /></p>
            </div>
            <textarea
            placeholder={i18n.t("examples.eGOutOfServiceAreaOrMissing")}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-rose-500/15 focus:border-rose-500 transition resize-none" />
          
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
              type="button"
              onClick={() => setIsRejectOpen(false)}
              className="h-9 px-4 rounded-xl border border-gray-200 bg-white text-xs font-bold text-gray-700 hover:bg-gray-50 cursor-pointer">
                <Trans i18nKey="formActions.cancel" />
              
            </button>
              <button
              type="button"
              onClick={handleRejectConfirm}
              className="h-9 px-4 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!rejectReason.trim() || Boolean(actionLoading)}>
              
                {actionLoading === 'reject' ? i18n.t('fsm.rejecting') : <Trans i18nKey="fsm.confirmReject" />}
              </button>
            </div>
          </div>
        </div>
      }

      <QuotationDetailsModal
        open={quotationDetailOpen}
        onOpenChange={(open) => {
          setQuotationDetailOpen(open);
          if (!open) setSelectedQuotation(null);
        }}
        quotation={selectedQuotation}
      />

    </AppLayout>);

}
